/**
 * Registry of the background operations each mounted BgTransformer can run
 * on its own template — cover-fit, flip, rotate. The scope bar in the sidebar
 * applies one operation to every banner in scope through this registry, so
 * "fit all backgrounds" reuses exactly the code the per-banner button runs.
 * A default save reads `bakeSource()` from the same registry to cut each
 * positioned banner (libs/bakedBg.ts), and the copy to other products reads
 * `positionSource()` (DefaultScenePanel/replicateBg.ts). A ready cut (BgTransformer/templateBg.ts)
 * has no positioning and registers nothing.
 * Module state, like libs/logoOverrides: a template registers on mount and
 * withdraws on unmount.
 *
 * The set of registered templates — the banners that have positioning — can
 * be watched (`subscribeBgOps` / `bgOpsSnapshot`, for useSyncExternalStore).
 */
const ops = new Map();
const listeners = new Set();
let snapshot = Object.freeze([]);
let publishPending = false;

/**
 * Tell the watchers once per task, and only when the set of ids changed. A
 * template re-registers on each change of its crop (withdraw, then register):
 * that is no change, so a drag does not wake the watchers.
 */
function schedulePublish() {
  if (publishPending) return;
  publishPending = true;
  queueMicrotask(() => {
    publishPending = false;
    const next = [...ops.keys()].sort();
    if (next.length === snapshot.length && next.every((id, i) => id === snapshot[i])) return;
    snapshot = Object.freeze(next);
    for (const listener of listeners) listener();
  });
}

export function registerBgOps(templateId, templateOps) {
  const isNew = !ops.has(templateId);
  ops.set(templateId, templateOps);
  if (isNew) schedulePublish();
  return () => {
    if (ops.get(templateId) === templateOps) {
      ops.delete(templateId);
      schedulePublish();
    }
  };
}

export function getBgOps(templateId) {
  return ops.get(templateId) ?? null;
}

export function registeredBgTemplateIds() {
  return [...ops.keys()];
}

/** Watch the set of registered templates; returns the unsubscribe. */
export function subscribeBgOps(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The registered template ids, sorted; the same array until the set changes. */
export function bgOpsSnapshot() {
  return snapshot;
}
